import { useMemo } from 'react';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { Appearance } from '@stripe/stripe-js';
import { STYLE_DEFAULTS } from '../../common';

type PaymentAppearanceProps = {
  color?: string;
  accentColor?: string;
};

export function usePaymentAppearances({
  color = STYLE_DEFAULTS.textColor,
  accentColor = STYLE_DEFAULTS.accentColor
}: PaymentAppearanceProps): {
  appearance: Appearance;
  disabledAppearance: Appearance;
} {
  return useMemo(() => {
    const tabBorderColor = getColorWithAlpha(color, 0.2);
    const hoverTabBorderColor = getColorWithAlpha(color, 0.5);

    const inputLabelColor = getColorWithAlpha(color, 0.5);
    const inputPlaceholderColor = getColorWithAlpha(color, 0.3);
    const inputBorderColor = getColorWithAlpha(color, 0.2);

    const inputDisabledColor = getColorWithAlpha(color, 0.5);
    const inputDisabledBackgroundColor = getColorWithAlpha(color, 0.05);
    const inputDisabledBorderColor = getColorWithAlpha(color, 0.2);

    const actionHoverBackgroundColor = getColorWithAlpha(accentColor, 0.15);

    const menuActionBackgroundColor = getColorWithAlpha(color, 0.1);
    const menuActionHoverBackgroundColor = getColorWithAlpha(color, 0.2);

    const appearance: Appearance = {
      labels: 'floating',
      variables: {
        colorPrimary: color,
        colorText: color,
        colorDanger: STYLE_DEFAULTS.errorColor,
        tabSpacing: '10px',
        gridColumnSpacing: '20px',
        gridRowSpacing: '20px',
        blockLogoColor: color,
        fontSizeBase: '16px',
        fontLineHeight: '22px'
      },
      rules: {
        '.Tab': {
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          color: color,

          boxShadow: `0 0 0 1px ${tabBorderColor}`
        },

        '.Tab:hover': {
          boxShadow: `0 0 0 1px ${hoverTabBorderColor}`
        },

        '.Tab--selected, .Tab:focus, .Tab:active, .Tab--selected:focus': {
          boxShadow: `0 0 0 2px ${accentColor}`
        },

        '.TabLabel': {
          fontWeight: '500',
          fontSize: '15px',
          lineHeight: '20px'
        },

        '.TabIcon': {
          fill: color
        },

        '.Label, .Label--resting': {
          color: inputLabelColor
        },

        '.Input': {
          padding: '8px 16px',
          fontSize: '16px',
          lineHeight: '22px'
        },

        '.Input, .PickerItem, .CheckboxInput': {
          padding: '16px',
          borderRadius: '8px',
          border: 'none',
          outline: 'none',
          color: color,
          backgroundColor: 'transparent',
          fontSize: '16px',
          lineHeight: '22px',
          boxShadow: `0 0 0 1px ${inputBorderColor}`
        },

        '.Input--invalid': {
          color: color,
          boxShadow: `0 0 0 1px ${STYLE_DEFAULTS.errorColor}`
        },

        '.Input--invalid:focus': {
          color: color,
          boxShadow: `0 0 0 1px ${STYLE_DEFAULTS.errorColor}`
        },

        '.Input:focus, .PickerItem--selected': {
          color: color,
          backgroundColor: 'transparent',
          boxShadow: `0 0 0 1px ${accentColor}`
        },

        '.Input:disabled': {
          color: inputDisabledColor,
          backgroundColor: inputDisabledBackgroundColor,
          boxShadow: `0 0 0 1px ${inputDisabledBorderColor}`
        },

        '.Input::placeholder': {
          color: inputPlaceholderColor,
          fontSize: '16px'
        },

        '.Checkbox': {
          marginTop: '20px'
        },

        '.CheckboxInput': {
          borderRadius: '4px'
        },

        '.CheckboxInput:focus': {
          outline: 'none',
          boxShadow: `0 0 0 1px ${accentColor}`
        },

        '.CheckboxInput--checked': {
          backgroundColor: accentColor
        },

        '.Error': {
          color: STYLE_DEFAULTS.errorColor,
          fontSize: '13px',
          lineHeight: '16px',
          marginTop: '8px'
        },

        '.PickerItem:hover, .PickerItem--selected:hover': {
          backgroundColor: 'transparent',
          boxShadow: `0 0 0 1px ${inputBorderColor}`
        },

        '.Action': {
          color: accentColor,
          padding: '4px',
          boxShadow: 'none',
          fontWeight: '500',
          fontSize: '13px',
          lineHeight: '16px'
        },

        '.Action:hover': {
          backgroundColor: actionHoverBackgroundColor
        },

        '.Input .Action:hover': {
          backgroundColor: 'none'
        },

        '.Block': {
          padding: '12px',
          borderRadius: '8px',
          border: 'none',
          outline: 'none',
          color: color,
          fill: color,
          backgroundColor: 'transparent',
          boxShadow: `0 0 0 1px ${inputBorderColor}`
        },

        '.BlockDivider': {
          backgroundColor: inputBorderColor
        },

        '.MenuAction, .MenuIcon': {
          color: color
        },

        '.MenuAction': {
          backgroundColor: menuActionBackgroundColor
        },

        '.MenuAction:hover, .MenuIcon:hover': {
          backgroundColor: menuActionHoverBackgroundColor
        },

        '.Label--floating': {
          fontSize: '11px',
          lineHeight: '12px',
          paddingBottom: '0px',
          marginBottom: '0px'
        }
      }
    };

    const disabledAppearance: Appearance = {
      ...appearance,
      rules: {
        ...appearance.rules,
        '.Input, .PickerItem, .CheckboxInput': {
          ...appearance?.rules?.['.Input, .PickerItem, .CheckboxInput'],
          ...appearance?.rules?.['.Input:disabled']
        }
      }
    };

    return { appearance, disabledAppearance };
  }, [color, accentColor]);
}
